import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ALL_NAV_ITEMS, ENCYCLOPEDIA_MENU, ICON_CLASS } from "./navConstants";
import { NavSettings } from "@/hooks/useNavSettings";

interface DesktopMenuProps {
  navSettings: NavSettings | undefined;
  isNavReady: boolean;
}

export function DesktopMenu({ navSettings, isNavReady }: DesktopMenuProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { nav_order: navOrder, nav_visibility: navVisibility } =
    navSettings || {};

  const isEncyclopediaActive = location.pathname.includes("/encyclopedia/");
  const isEncyclopediaVisible = navVisibility?.encyclopedia !== false;

  const visibleEncyclopediaMenu = React.useMemo(() => {
    return ENCYCLOPEDIA_MENU.filter((item) => {
      if (navVisibility) {
        return navVisibility[item.key as keyof typeof navVisibility] !== false;
      }
      return true;
    });
  }, [navVisibility]);

  const renderLink = (key: string) => {
    const item = ALL_NAV_ITEMS[key];
    if (!item) return null;
    if (
      navVisibility &&
      navVisibility[key as keyof typeof navVisibility] === false
    )
      return null;

    const isActive =
      location.pathname === item.path ||
      (item.path !== "/" && location.pathname.startsWith(item.path));

    return (
      <Link
        key={item.key}
        to={item.path}
        className={cn(
          "flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-foreground text-background"
            : "text-foreground hover:bg-accent hover:text-foreground",
        )}
      >
        {item.icon}
        {t(item.labelKey, item.defaultLabel)}
      </Link>
    );
  };

  if (!isNavReady) {
    // 页面加载中不渲染中间菜单，避免后台隐藏的项短暂闪烁
    return <div className="hidden lg:flex flex-1" />;
  }

  return (
    <div className="hidden lg:flex items-center gap-2 justify-center">
      {navOrder?.map((key) => {
        if (key === "encyclopedia") {
          if (!isEncyclopediaVisible || visibleEncyclopediaMenu.length === 0)
            return null;
          return (
            <DropdownMenu key="encyclopedia">
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors outline-none",
                    isEncyclopediaActive
                      ? "bg-foreground text-background"
                      : "text-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  <BookOpen className={ICON_CLASS} />
                  {t("nav.encyclopedia", "百科")}
                  <ChevronDown className="size-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-40">
                {visibleEncyclopediaMenu.map((item) => (
                  <DropdownMenuItem key={item.key} asChild>
                    <Link
                      to={item.path}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      {item.icon}
                      {t(item.labelKey, item.defaultLabel)}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }

        if (key === "encyclopedia_articles" || key === "encyclopedia_faqs") {
          return null;
        }

        return renderLink(key);
      })}
    </div>
  );
}
